import { getBundledCastleSkinFallbackUrl } from "../constants/skins";

const loadedCastleSkinUrls = new Set();
const inFlightCastleSkinLoads = new Map();
const CASTLE_LOAD_TIMEOUT_MS = 12_000;

export const preloadCastleSkinUrl = (rawUrl) => {
  const url = String(rawUrl || "").trim();
  if (!url || typeof window === "undefined" || typeof window.Image !== "function") {
    return Promise.reject(new Error("Castle skin image loading is unavailable"));
  }

  if (loadedCastleSkinUrls.has(url)) {
    return Promise.resolve(url);
  }

  const existingLoad = inFlightCastleSkinLoads.get(url);
  if (existingLoad) {
    return existingLoad;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const image = new window.Image();
    let settled = false;
    const finish = (error) => {
      if (settled) return;
      settled = true;
      clearTimeout(timeout);
      image.onload = null;
      image.onerror = null;
      inFlightCastleSkinLoads.delete(url);
      if (error) reject(error);
      else {
        loadedCastleSkinUrls.add(url);
        resolve(url);
      }
    };
    // Profile data must remain usable if the remote castle or its decode
    // stalls. Callers already render the actual profile over a plain surface
    // when this optional asset fails; a later open can retry the same image.
    const timeout = setTimeout(() => finish(new Error(`Timed out loading castle skin: ${url}`)), CASTLE_LOAD_TIMEOUT_MS);
    image.decoding = "async";
    image.onload = async () => {
      if (typeof image.decode === "function") {
        try {
          await image.decode();
        } catch {
          // A completed image load is still safe to display when a WebView
          // does not support or rejects the optional decode hint.
        }
      }
      finish();
    };
    image.onerror = () => {
      finish(new Error(`Failed to load castle skin: ${url}`));
    };
    image.src = url;
  });

  inFlightCastleSkinLoads.set(url, loadPromise);
  return loadPromise;
};

export const preloadCastleSkinUrls = (urls) =>
  Promise.allSettled(
    Array.from(new Set((urls || []).filter(Boolean))).map(preloadCastleSkinUrl)
  );

export const applyBundledCastleSkinFallback = (imageElement) => {
  if (!imageElement) return false;
  const fallbackUrl = getBundledCastleSkinFallbackUrl();
  if (imageElement.src === fallbackUrl || imageElement.dataset?.castleFallbackApplied === "true") {
    return false;
  }

  if (imageElement.dataset) {
    imageElement.dataset.castleFallbackApplied = "true";
  }
  imageElement.src = fallbackUrl;
  return true;
};

export const __resetCastleSkinAssetCacheForTests = () => {
  loadedCastleSkinUrls.clear();
  inFlightCastleSkinLoads.clear();
};
